import { useMemo } from 'react';

import {
  IMarketingAlignedPoint,
  IMarketingBucket,
  marketingBuckets,
  marketingCompareInfoRange,
  marketingCompareInput,
  marketingCompareRange,
  marketingComparedPrevious,
  marketingResolveStep,
  marketingServerAligned,
  MarketingCompareMode,
  MarketingStep,
} from './marketingCompareModel';
import { IMarketingRange, MarketingPeriodPreset } from './marketingModel';
import {
  IMarketingCompareInfo,
  IMarketingFilter,
  IMarketingMetrics,
  IMarketingOverview,
  IMarketingSeriesPoint,
  MarketingGranularityCode,
} from './types';
import { IMarketingCompareRequestInput } from './marketingApi';
import {
  IMarketingQueryOptions,
  IMarketingQueryState,
  useMarketingOverview,
  useMarketingSeries,
} from './useMarketingReports';

/**
 * ТЗ 0103 — сравнение периодов для экрана (design-erp §6.2). Компоненты
 * получают `{ current, previous, aligned, compareRange, partial }` и не
 * знают, откуда числа.
 *
 * Этап 2: ОДИН запрос с `compare` — сервер сам считает окно сравнения
 * (`compareInfo`), отдаёт `previous` итогов и у каждой точки ряда — корзину
 * сравнения того же номера с её датами. Фронт окно не считает: прежний
 * второй запрос за «своё» окно ушёл. Модель (`marketingCompareRange`)
 * осталась только оценкой подписи, пока ответ едет.
 */

export interface IMarketingCompareInput {
  filter: IMarketingFilter;
  range: IMarketingRange;
  preset: MarketingPeriodPreset;
  compare: MarketingCompareMode;
  compareFrom: string | null;
  compareTo: string | null;
  step: MarketingStep | null;
}

export interface IMarketingCompareView {
  /** Шаг ряда для запроса. */
  granularity: MarketingGranularityCode;
  /** Вход `compare` запроса; `null` — сравнения нет. */
  request: IMarketingCompareRequestInput | null;
  /** Корзины окна по шагу — ось, пока ответ едет. */
  buckets: IMarketingBucket[];
  /** Оценка окна сравнения (подпись до ответа); `null` — сравнения нет. */
  estimate: IMarketingRange | null;
}

/** Шаг, вход запроса и корзины — без запросов (для тестов и панелей). */
export const marketingCompareView = ({
  range,
  preset,
  compare,
  compareFrom,
  compareTo,
  step,
}: IMarketingCompareInput): IMarketingCompareView => {
  const granularity = marketingResolveStep(step, range);
  const request = marketingCompareInput(
    compare,
    compareFrom,
    compareTo,
    preset,
  );
  return {
    granularity,
    request,
    buckets: marketingBuckets(range, granularity),
    estimate: request
      ? marketingCompareRange({
          preset,
          range,
          mode: compare,
          custom:
            compareFrom && compareTo
              ? { from: compareFrom, to: compareTo }
              : null,
        })
      : null,
  };
};

export const useMarketingCompareView = (
  input: IMarketingCompareInput,
): IMarketingCompareView => {
  const {
    filter,
    range,
    preset,
    compare,
    compareFrom,
    compareTo,
    step,
  } = input;
  return useMemo(
    () =>
      marketingCompareView({
        filter,
        range,
        preset,
        compare,
        compareFrom,
        compareTo,
        step,
      }),
    [filter, range, preset, compare, compareFrom, compareTo, step],
  );
};

export interface IMarketingCompareResult extends IMarketingCompareView {
  /** Окно сравнения сервера; `null` — сравнения нет (или ответ не пришёл). */
  compareInfo: IMarketingCompareInfo | null;
  /** Окно сравнения сервера датами; `null` — сравнения нет. */
  compareRange: IMarketingRange | null;
  /** Ряд по корзинам: текущая и та же по номеру корзина сравнения. */
  aligned: IMarketingAlignedPoint<IMarketingSeriesPoint>[];
  /** Окно сравнения собрано не целиком («сравнение неполное»). */
  partial: boolean;
  /** Сравнение обрезано по тот же день (окно ещё идёт). */
  cutToDate: boolean;
}

export interface IMarketingCompareOverview extends IMarketingCompareResult {
  /** Обзор окна со сравнением сервера (`marketingOverview(compare)`). */
  overview: IMarketingQueryState<IMarketingOverview>;
  /** Итоги текущего окна. */
  current: IMarketingMetrics | null;
  /** Итоги окна сравнения; `null` — сравнения нет. */
  previous: IMarketingMetrics | null;
}

const resultOf = (
  view: IMarketingCompareView,
  info: IMarketingCompareInfo | null,
  aligned: IMarketingAlignedPoint<IMarketingSeriesPoint>[],
): IMarketingCompareResult => ({
  ...view,
  compareInfo: info,
  compareRange: marketingCompareInfoRange(info),
  aligned,
  partial: Boolean(info?.partial),
  cutToDate: Boolean(info?.cutToDate),
});

/**
 * Обзор окна со сравнением — одним запросом. «Без сравнения» — без
 * аргумента `compare`: `previous` сервера тогда — окно той же длины перед,
 * и экран его НЕ показывает дельтой (`marketingComparedPrevious`).
 */
export const useMarketingCompareOverview = (
  input: IMarketingCompareInput,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingCompareOverview => {
  const view = useMarketingCompareView(input);
  const { granularity, request, buckets } = view;
  const overview = useMarketingOverview(input.filter, granularity, {
    enabled,
    compare: request,
  });
  const { data } = overview;
  // Ответ без `compareInfo` при включённом сравнении — прошлый ответ
  // (`previousData`) другой настройки: окна сравнения у него нет.
  const info = request ? data?.compareInfo ?? null : null;
  const series = data?.series ?? null;
  const aligned = useMemo(
    () => marketingServerAligned(series, granularity, buckets),
    [series, granularity, buckets],
  );
  return {
    ...resultOf(view, info, aligned),
    overview,
    current: data?.current ?? null,
    previous: marketingComparedPrevious(data?.previous, info),
  };
};

export interface IMarketingCompareSeries extends IMarketingCompareResult {
  series: IMarketingQueryState<IMarketingSeriesPoint[]>;
}

/**
 * Ряд окна («неделя к неделе», `ltab=periods`) — одним запросом; с
 * `withCompare` у точки корзина сравнения того же номера (год назад —
 * второй строкой ячейки). `withCompare: false` — дельта к соседней колонке
 * считается по самому ряду, сравнение не спрашивается.
 */
export const useMarketingCompareSeries = (
  input: IMarketingCompareInput,
  {
    enabled = true,
    withCompare = true,
  }: IMarketingQueryOptions & { withCompare?: boolean } = {},
): IMarketingCompareSeries => {
  const view = useMarketingCompareView(input);
  const { granularity, buckets } = view;
  const request = withCompare ? view.request : null;
  const series = useMarketingSeries(input.filter, granularity, {
    enabled,
    compare: request,
  });
  const points = series.data;
  const aligned = useMemo(
    () => marketingServerAligned(points, granularity, buckets),
    [points, granularity, buckets],
  );
  // У ряда нет `compareInfo`: окно сравнения — от первой до последней
  // корзины сравнения в ответе.
  const info = useMemo<IMarketingCompareInfo | null>(() => {
    if (!request) return null;
    const pairs = aligned.filter((point) => point.compareBucket);
    if (!pairs.length) return null;
    const first = pairs[0].compareBucket as IMarketingBucket;
    const last = pairs[pairs.length - 1].compareBucket as IMarketingBucket;
    return {
      mode: request.mode,
      unit: request.unit ? request.unit.toLowerCase() : null,
      dateFrom: first.from,
      dateTo: last.to,
      aligned: pairs.length === aligned.length,
      partial: false,
      cutToDate: false,
    };
  }, [request, aligned]);
  return { ...resultOf({ ...view, request }, info, aligned), series };
};
